import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getSlotAthletes,
  markAttendance,
  removeAttendance,
  KioskProgram,
  KioskSlot,
  KioskAthlete,
} from "../../api/kioskAPI";
import { KioskSettings } from "./KioskMode";
import KioskMakeupModal from "./KioskMakeupModal";
import { useParticipantLabel } from "../../hooks/useParticipantLabel";
import "./KioskAthleteCards.css";
import { captureKioskError } from "../../utils/sentry";

interface KioskAthleteCardsProps {
  program: KioskProgram;
  slot: KioskSlot;
  onBack: () => void;
  onAttendanceComplete: () => void;
  settings: KioskSettings;
}

interface PendingAttendance {
  athleteId: string;
  timeoutId: NodeJS.Timeout;
  startTime: number;
}

const UNDO_WINDOW_MS = 3000; // 3 seconds

/** The server's own reason for a refused save, as a sentence, or nothing. */
const reasonOf = (err: unknown): string => {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
  const text = data?.error || data?.message;
  return typeof text === "string" && text.trim() ? `${text.trim().replace(/[.!]?$/, ".")} ` : "";
};

const KioskAthleteCards: React.FC<KioskAthleteCardsProps> = ({
  program,
  slot,
  onBack,
  onAttendanceComplete,
  settings,
}) => {
  const participant = useParticipantLabel();
  const [athletes, setAthletes] = useState<KioskAthlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ presentCount: number; totalAthletes: number } | null>(null);
  const [pendingAttendances, setPendingAttendances] = useState<Map<string, PendingAttendance>>(new Map());
  const [countdowns, setCountdowns] = useState<Map<string, number>>(new Map());
  const [timeParts, setTimeParts] = useState<{ hour: string; minute: string; ampm: string }>({ hour: "", minute: "", ampm: "" });
  const [showMakeupModal, setShowMakeupModal] = useState(false);
  // A tap that did not save. It used to go only to Sentry: the card turned
  // back without a word and the athlete walked off thinking they were in.
  const [saveError, setSaveError] = useState<string | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Realtime clock with timezone support
  useEffect(() => {
    const updateClock = () => {
      try {
        const now = new Date();
        const hour = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          hour: "numeric",
          hour12: true,
        }).replace(/\s*(AM|PM)$/i, "");
        const minute = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          minute: "2-digit",
        });
        const ampm = now.toLocaleTimeString("en-US", {
          timeZone: settings.timezone,
          hour: "numeric",
          hour12: true,
        }).match(/(AM|PM)$/i)?.[0] || "";
        setTimeParts({ hour, minute, ampm });
      } catch {
        const now = new Date();
        const hour = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        }).replace(/\s*(AM|PM)$/i, "");
        const minute = now.toLocaleTimeString("en-US", {
          minute: "2-digit",
        });
        const ampm = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        }).match(/(AM|PM)$/i)?.[0] || "";
        setTimeParts({ hour, minute, ampm });
      }
    };

    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [settings.timezone]);

  const loadAthletes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSlotAthletes(slot.id);
      if (response.success) {
        setAthletes(response.data);
        setMeta({
          presentCount: response.meta.presentCount,
          totalAthletes: response.meta.totalAthletes,
        });
      } else {
        setError("Failed to load athletes");
      }
    } catch (err) {
      setError("Unable to connect. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [slot.id]);

  useEffect(() => {
    loadAthletes();
  }, [loadAthletes]);

  // Animation frame for smooth countdown updates
  useEffect(() => {
    const updateCountdowns = () => {
      const now = Date.now();
      const newCountdowns = new Map<string, number>();
      let hasActive = false;

      pendingAttendances.forEach((pending, athleteId) => {
        const elapsed = now - pending.startTime;
        const remaining = Math.max(0, UNDO_WINDOW_MS - elapsed);
        if (remaining > 0) {
          newCountdowns.set(athleteId, remaining);
          hasActive = true;
        }
      });

      setCountdowns(newCountdowns);

      if (hasActive) {
        animationFrameRef.current = requestAnimationFrame(updateCountdowns);
      }
    };

    if (pendingAttendances.size > 0) {
      animationFrameRef.current = requestAnimationFrame(updateCountdowns);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [pendingAttendances]);

  const handleAthleteClick = useCallback(
    async (athlete: KioskAthlete) => {
      // Check if there's a pending undo for this athlete
      const pending = pendingAttendances.get(athlete.id);
      if (pending) {
        // Cancel the pending save and undo
        clearTimeout(pending.timeoutId);
        setPendingAttendances((prev) => {
          const next = new Map(prev);
          next.delete(athlete.id);
          return next;
        });

        // Remove attendance immediately
        try {
          await removeAttendance(athlete.id, slot.id);
          setAthletes((prev) =>
            prev.map((a) =>
              a.id === athlete.id ? { ...a, isPresent: false } : a
            )
          );
          setMeta((prev) =>
            prev ? { ...prev, presentCount: Math.max(0, prev.presentCount - 1) } : prev
          );
        } catch (err) {
          captureKioskError(err, "removeAttendance", { slotId: slot.id });
          setSaveError(
            `Couldn't undo ${athlete.firstName}'s check-in. ${reasonOf(err)}Please tell a staff member.`
          );
        }
        return;
      }

      // If already present (without pending), do nothing
      if (athlete.isPresent) {
        return;
      }

      setSaveError(null);
      // Mark as present optimistically
      setAthletes((prev) =>
        prev.map((a) =>
          a.id === athlete.id ? { ...a, isPresent: true } : a
        )
      );
      setMeta((prev) =>
        prev ? { ...prev, presentCount: prev.presentCount + 1 } : prev
      );

      // Play success sound
      playSuccessSound();

      // Set up the undo window timeout
      const timeoutId = setTimeout(async () => {
        // Commit the attendance to server
        let success = false;
        try {
          await markAttendance(athlete.id, slot.id);
          success = true;
        } catch (err) {
          captureKioskError(err, "markAttendance", { slotId: slot.id });
          setSaveError(
            `${athlete.firstName}'s check-in did not save. ${reasonOf(err)}Please try again or tell a staff member.`
          );
          // Revert on error
          setAthletes((prev) =>
            prev.map((a) =>
              a.id === athlete.id ? { ...a, isPresent: false } : a
            )
          );
          setMeta((prev) =>
            prev ? { ...prev, presentCount: Math.max(0, prev.presentCount - 1) } : prev
          );
        }

        // Remove from pending
        setPendingAttendances((prev) => {
          const next = new Map(prev);
          next.delete(athlete.id);
          return next;
        });

        // Navigate back to programs after successful attendance
        if (success) {
          onAttendanceComplete();
        }
      }, UNDO_WINDOW_MS);

      // Add to pending
      setPendingAttendances((prev) => {
        const next = new Map(prev);
        next.set(athlete.id, {
          athleteId: athlete.id,
          timeoutId,
          startTime: Date.now(),
        });
        return next;
      });
    },
    [pendingAttendances, slot.id]
  );

  const playSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.1);

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch {
      // Audio not supported, continue silently
    }
  };

  const handleMakeupSuccess = useCallback((athleteName: string) => {
    setShowMakeupModal(false);
    playSuccessSound();
    // Reload athletes to show updated count
    loadAthletes();
    // Navigate back after a brief delay
    setTimeout(() => {
      onAttendanceComplete();
    }, 1500);
  }, [loadAthletes, onAttendanceComplete]);

  const getCountdownProgress = (athleteId: string): number => {
    const remaining = countdowns.get(athleteId) || 0;
    return remaining / UNDO_WINDOW_MS;
  };

  if (loading) {
    return (
      <div className="kiosk-screen kiosk-athletes">
        <div className="kiosk-loading">
          <div className="kiosk-loading__spinner" />
          <p>Loading {participant.pluralLower}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kiosk-screen kiosk-athletes">
        <div className="kiosk-empty">
          <div className="kiosk-empty__icon">&#9888;</div>
          <p>{error}</p>
          <button className="kiosk-retry-btn" onClick={loadAthletes}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kiosk-screen kiosk-athletes">
      <div className="kiosk-header">
        <button className="kiosk-header__back" onClick={onBack}>
          <span className="kiosk-header__back-icon">&#8249;</span>
          Back
        </button>
        <div className="kiosk-header__title">
          <h1>Tap Your Name</h1>
          <p>
            {program.label} • {meta?.presentCount || 0} of {meta?.totalAthletes || 0} present
          </p>
        </div>
        <div className="kiosk-header__clock-wrapper">
          <span className="kiosk-header__clock kiosk-header__clock--static">
            {timeParts.hour}<span className="kiosk-clock__colon">:</span>{timeParts.minute} {timeParts.ampm}
          </span>
        </div>
      </div>

      {saveError && (
        <div className="kiosk-save-error" role="alert">
          <span>{saveError}</span>
          <button type="button" className="kiosk-save-error__dismiss" onClick={() => setSaveError(null)}>
            OK
          </button>
        </div>
      )}

      {athletes.length === 0 ? (
        <div className="kiosk-empty">
          <div className="kiosk-empty__icon">&#128101;</div>
          <p>No {participant.pluralLower} enrolled in this slot</p>
        </div>
      ) : (
        <div className="kiosk-cards kiosk-athlete-cards">
          {athletes.map((athlete) => {
            const isPending = pendingAttendances.has(athlete.id);
            const progress = getCountdownProgress(athlete.id);

            return (
              <button
                key={athlete.id}
                className={`kiosk-athlete-card ${
                  athlete.isPresent ? "kiosk-athlete-card--present" : ""
                } ${isPending ? "kiosk-athlete-card--pending" : ""}`}
                onClick={() => handleAthleteClick(athlete)}
              >
                {isPending && (
                  <div
                    className="kiosk-athlete-card__progress"
                    style={{
                      transform: `scaleX(${progress})`,
                    }}
                  />
                )}
                <div className="kiosk-athlete-card__content">
                  <span className="kiosk-athlete-card__name">
                    {athlete.firstName} {athlete.lastName}
                  </span>
                  {athlete.isPresent && !isPending && (
                    <span className="kiosk-athlete-card__checkmark">&#10003;</span>
                  )}
                  {isPending && (
                    <span className="kiosk-athlete-card__undo">Tap to undo</span>
                  )}
                </div>
              </button>
            );
          })}

          {/* Make Up Card — hidden when the tenant disables
              settings.allow_makeups. Default behavior (true)
              preserves the kiosk's makeup affordance for clubs like
              GoY. */}
          {settings.allowMakeups && (
            <button
              className="kiosk-athlete-card kiosk-athlete-card--makeup"
              onClick={() => setShowMakeupModal(true)}
            >
              <div className="kiosk-athlete-card__content">
                <span className="kiosk-athlete-card__makeup-icon">&#128260;</span>
                <span className="kiosk-athlete-card__name">Make Up</span>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Makeup Modal */}
      {settings.allowMakeups && showMakeupModal && (
        <KioskMakeupModal
          slotId={slot.id}
          programLabel={program.label}
          onClose={() => setShowMakeupModal(false)}
          onSuccess={handleMakeupSuccess}
        />
      )}
    </div>
  );
};

export default KioskAthleteCards;
